import React, { useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import PropTypes from 'prop-types';
import { fr } from 'date-fns/locale';
import { getUserName, formatDate } from 'helpers';
import { useAPI, useGetPilgrim, useGetUser } from 'hooks';
import Locales from 'locales';
import { CATEGORY_NON_RESIDENT } from 'data';
import { DEPARTEMENTS } from 'data/Pilgrims';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription } from 'components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Textarea } from 'components/ui/textarea';
import { cn } from 'lib/utils';
import { AlertCircle, ArrowRight, CalendarIcon, ExternalLink } from 'lucide-react';

const EditTripModal = ({
  isOpened = false,
  pilgrimData = null,
  pilgrimEligibilityMinAge = null,
  categories = null,
  openCategoriesModal = null,
  isPilgrimUser = false,
  isPreviousCampaignPilgrim = false,
  isReadOnly = false,
  trackCampaignWrite = false,
  closeModal = () => {},
}) => {
  const { isAgency } = useGetUser();

  const {
    id,
    firstname,
    lastname,
    npi,
    nic,
    passport,
    passportExpirationDate,
    birthdate,
    note,
    categoryId,
    isInvoiceFullyPaid,
    isApprovedForMAECStep,
    hasCompletedMedicalCheckup,
    isInternational,
    isAssignFromPreviousCampaign,
    department,
  } = useGetPilgrim(pilgrimData);

  const [pilgrimNPI, setNPI] = useState(npi || '');
  const [pilgrimNIC, setPilgrimNIC] = useState(nic || '');
  const [pilgrimPassport, setPassport] = useState(passport || '');
  const [pilgrimFirstname, setFirstname] = useState(firstname || '');
  const [pilgrimLastname, setLastname] = useState(lastname || '');
  const [pilgrimNote, setPilgrimNote] = useState(note || '');
  const [pilgrimBirthdate, setBirthdate] = useState(birthdate ? new Date(birthdate) : null);
  const [pilgrimPassportExpirationDate, setPassportExpirationDate] = useState(
    passportExpirationDate ? new Date(passportExpirationDate) : null
  );
  const [pilgrimCategory, setPilgrimCategory] = useState(categoryId || '');
  const [pilgrimIsInternational, setpilgrimIsInternational] = useState(isInternational || false);
  const [pilgrimDepartment, setPilgrimDepartment] = useState(department || '');
  const [pilgrimSex, setPilgrimSex] = useState(
    ['M', 'F'].includes(pilgrimData?.pilgrim?.sex) ? pilgrimData.pilgrim.sex : ''
  );

  const [birthdateMonth, setBirthdateMonth] = useState(
    pilgrimBirthdate ? new Date(pilgrimBirthdate.getFullYear(), pilgrimBirthdate.getMonth()) : new Date()
  );
  const [expirationMonth, setExpirationMonth] = useState(
    pilgrimPassportExpirationDate
      ? new Date(pilgrimPassportExpirationDate.getFullYear(), pilgrimPassportExpirationDate.getMonth())
      : new Date(new Date().setMonth(new Date().getMonth() + 6))
  );

  const [npiError, setNpiError] = useState(false);
  const [nicError, setNicError] = useState(false);
  const [passportError, setPassportError] = useState(false);
  const [emptyFieldError, setEmptyFieldError] = useState(false);
  const [emptyNPIError, setEmptyNPIError] = useState(false);
  const [emptyNicError, setEmptyNicError] = useState(false);
  const [birthDateError, setBirthDateError] = useState(null);
  const [passportExpirationDateError, setPassportExpirationDateError] = useState(null);
  const [passportRequiredError, setPassportRequiredError] = useState(false);
  const [passportExpirationDateRequiredError, setPassportExpirationDateRequiredError] = useState(false);
  const [openBirthdatePopover, setOpenBirthdatePopover] = useState(false);
  const [openPassportPopover, setOpenPassportPopover] = useState(false);

  const passportMinExpirationDate = new Date();
  passportMinExpirationDate.setMonth(passportMinExpirationDate.getMonth() + 6);

  const hasCompletedMedicalCheckupOrFullPayment = hasCompletedMedicalCheckup || isInvoiceFullyPaid;
  const disableUserIdentityFields =
    isApprovedForMAECStep || (isAgency && hasCompletedMedicalCheckup) || (isAgency && isAssignFromPreviousCampaign);
  const disablePassportField = isApprovedForMAECStep;
  const disableCategoryField = (isAgency && isInvoiceFullyPaid) || isApprovedForMAECStep;

  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  // Calculate the pilgrim age based on his birth date
  const getAge = (date) => {
    if (!date) return 0;
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > date.getMonth() || (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());
    if (!hasHadBirthdayThisYear) age--;
    return age;
  };

  const updatedPilgrim = {
    pilgrim: {
      first_name: pilgrimFirstname.trim(),
      last_name: pilgrimLastname ? pilgrimLastname.toUpperCase().trim() : '',
      sex: pilgrimSex,
      npi: pilgrimNPI,
      nic: pilgrimNIC,
      passport: pilgrimPassport ? pilgrimPassport.toUpperCase() : '',
      birthdate: pilgrimBirthdate ? formatDate(pilgrimBirthdate, 'yyyy-MM-dd') : null,
      passport_expiration: pilgrimPassportExpirationDate
        ? formatDate(pilgrimPassportExpirationDate, 'yyyy-MM-dd')
        : null,
      is_international: pilgrimIsInternational,
      department: pilgrimDepartment === '' ? null : pilgrimDepartment,
    },
    note: pilgrimNote.trim(),
    category_id: pilgrimCategory,
  };

  const { mutate: editPilgrim } = useMutation({
    meta: { agloCampaignWrite: trackCampaignWrite },
    mutationFn: async () => {
      await api.update('trip', updatedPilgrim.pilgrim, `${id}/pilgrim/update`);
      return api.update('trip', updatedPilgrim, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/trip_category_paid/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/'] });
      queryClient.invalidateQueries({ queryKey: ['agency/', 'is_sa'] });
      toast.success(
        Locales.formatString(Locales.trip.toast.pilgrim_updated, {
          fullname: getUserName(pilgrimFirstname, pilgrimLastname),
        })
      );
      closeModal();
    },
  });

  const { mutate: editPreviousCampaignPilgrim } = useMutation({
    meta: { agloCampaignWrite: trackCampaignWrite },
    mutationFn: () => api.update('trip/update/reported_trip', updatedPilgrim, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/trip_category_paid/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/previous_campaign/reported/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(
        Locales.formatString(Locales.trip.toast.pilgrim_updated, {
          fullname: getUserName(pilgrimFirstname, pilgrimLastname),
        })
      );
      closeModal();
    },
  });

  const handleEdit = () => {
    if (isReadOnly) return;
    if (isPreviousCampaignPilgrim) editPreviousCampaignPilgrim();
    else editPilgrim();
  };

  const validateForm = useCallback(
    (e) => {
      e.preventDefault();
      if (isReadOnly) return;

      const selectedCategoryLocal = categories.find((c) => c.id === pilgrimCategory);

      const isNPIMissing = pilgrimNPI.trim() === '';
      const isNICMissing = pilgrimNIC.trim() === '';
      const isPassportProvided = pilgrimPassport.trim() !== '';

      const npiCorrectFormat = isNPIMissing || /^[0-9]{10}$/.test(pilgrimNPI);
      const nicCorrectFormat = isNICMissing || /^[0-9]{10}$/.test(pilgrimNIC);
      const passportCorrectFormat = !isPassportProvided || /^[A-Z0-9]{8,9}$/.test(pilgrimPassport);

      const npiErrorLocal = pilgrimNPI !== '' && !npiCorrectFormat;
      const nicErrorLocal = pilgrimNIC !== '' && !nicCorrectFormat;
      const passportErrorLocal = isPassportProvided && !passportCorrectFormat;
      const emptyPassportOrNpiFieldErrorLocal = !isPassportProvided && isNPIMissing;
      const emptyNicErrorLocal = selectedCategoryLocal?.name.includes(CATEGORY_NON_RESIDENT) && isNICMissing;

      const emptyFieldErrorLocal =
        pilgrimFirstname.trim().length === 0 ||
        pilgrimLastname.trim().length === 0 ||
        (!isPreviousCampaignPilgrim && !pilgrimSex) ||
        pilgrimBirthdate === null ||
        emptyPassportOrNpiFieldErrorLocal;

      const passportRequiredErrorLocal = !isPassportProvided && pilgrimPassportExpirationDate !== null;
      const passportExpirationDateRequiredErrorLocal = isPassportProvided && pilgrimPassportExpirationDate === null;

      // Age verification
      let birthDateErrorLocal = null;
      if (pilgrimBirthdate) {
        const pilgrimAge = getAge(pilgrimBirthdate);
        if (pilgrimAge < pilgrimEligibilityMinAge) {
          birthDateErrorLocal = `L'âge minimum requis est de ${pilgrimEligibilityMinAge} ans.`;
        }
      } else {
        birthDateErrorLocal = 'Veuillez saisir une date de naissance.';
      }

      setBirthDateError(birthDateErrorLocal);
      setNpiError(npiErrorLocal);
      setPassportError(passportErrorLocal);
      setEmptyFieldError(emptyFieldErrorLocal);
      setPassportRequiredError(passportRequiredErrorLocal);
      setPassportExpirationDateRequiredError(passportExpirationDateRequiredErrorLocal);
      setNicError(nicErrorLocal);
      setEmptyNicError(emptyNicErrorLocal);
      setEmptyNPIError(isNPIMissing);

      if (
        !npiErrorLocal &&
        !emptyFieldErrorLocal &&
        !passportErrorLocal &&
        !passportRequiredErrorLocal &&
        !passportExpirationDateRequiredErrorLocal &&
        birthDateErrorLocal === null &&
        passportExpirationDateError === null &&
        !nicErrorLocal &&
        !emptyNicErrorLocal &&
        !isNPIMissing
      ) {
        handleEdit();
      }
    },
    [
      pilgrimNPI,
      pilgrimPassport,
      pilgrimFirstname,
      pilgrimLastname,
      pilgrimSex,
      isPreviousCampaignPilgrim,
      pilgrimBirthdate,
      pilgrimPassportExpirationDate,
      birthDateError,
      passportExpirationDateError,
      pilgrimNIC,
      pilgrimCategory,
    ]
  );

  const handleBirthDateChange = (date) => {
    if (!date) return;
    setBirthdate(date);
    setBirthdateMonth(new Date(date.getFullYear(), date.getMonth()));
    setBirthDateError(null);

    setTimeout(() => {
      setOpenBirthdatePopover(false);
    }, 10);
  };

  const handleExpirationDateChange = (date) => {
    if (!date) return;
    setPassportExpirationDate(date);
    setExpirationMonth(new Date(date.getFullYear(), date.getMonth()));
    setPassportExpirationDateError(null);
    setPassportExpirationDateRequiredError(false);

    setTimeout(() => {
      setOpenPassportPopover(false);
    }, 10);
  };

  const handlePassportChange = (value) => {
    setPassport(value.toUpperCase());
    setPassportRequiredError(false);
  };

  const handleSexChange = (value) => {
    setPilgrimSex(value);
  };

  useEffect(() => {
    const selectedCategory = categories.find((c) => c.id === pilgrimCategory);
    setpilgrimIsInternational(selectedCategory?.name.includes(CATEGORY_NON_RESIDENT));
  }, [pilgrimCategory]);

  return (
    <Dialog open={isOpened} onOpenChange={closeModal}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-[736px] flex-col gap-0 overflow-hidden rounded-[14px] border-gray-200 bg-white p-0 font-sans shadow-xl sm:max-h-[calc(100dvh-3rem)]">
        <DialogHeader className="shrink-0 space-y-1 border-b border-gray-200 px-[18px] py-4 pr-14 text-left sm:px-[26px] sm:py-5">
          <DialogTitle className="text-lg font-semibold text-gray-900">Modifier le pèlerin</DialogTitle>
          <DialogDescription className="text-[13px] text-gray-500">
            Renseignez les informations du pèlerin.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[18px] py-[17px] [scrollbar-color:#c9d3cc_transparent] [scrollbar-width:thin] sm:px-[26px] sm:py-[19px]">
          <div className="space-y-4">
            {isAgency && hasCompletedMedicalCheckupOrFullPayment && !isApprovedForMAECStep && (
              <Alert>
                <AlertCircle className="size-[16px] text-blue-700" />
                <AlertDescription>
                  <span className="text-blue-700">{Locales.trip.info.locked_health_or_paid}</span>
                </AlertDescription>
              </Alert>
            )}

            {isAgency && isAssignFromPreviousCampaign && (
              <Alert>
                <AlertCircle className="size-[16px] text-blue-700" />
                <AlertDescription>
                  <span className="text-blue-700">{Locales.trip.info.locked_previous_campign}</span>
                </AlertDescription>
              </Alert>
            )}

            {isApprovedForMAECStep && (
              <Alert>
                <AlertCircle className="size-[16px] text-blue-700" />
                <AlertDescription>
                  <span className="text-blue-700">
                    {Locales.formatString(Locales.trip.info.locked_visa, {
                      note: <strong>{Locales.base.field.note}</strong>,
                    })}
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {(emptyFieldError || passportRequiredError || passportExpirationDateRequiredError) && (
              <Alert variant="destructive">
                <AlertCircle className="size-[16px]" />
                <AlertDescription>
                  {emptyFieldError && Locales.trip.errors.emptyfield}
                  {passportRequiredError && Locales.trip.errors.passport_required}
                  {passportExpirationDateRequiredError && Locales.trip.errors.passport_expiration_date_required}
                </AlertDescription>
              </Alert>
            )}

            <section aria-labelledby="edit-identity-heading">
              <h3 id="edit-identity-heading" className="mb-3 text-[13px] font-semibold text-[#35473b]">
                Identité
              </h3>
              <div className="grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                <div className="space-y-[5px]">
                  <Label htmlFor="edit-firstname">{Locales.base.field.first_name}*</Label>
                  <Input
                    id="edit-firstname"
                    placeholder={Locales.base.field.placeholder.firstname}
                    value={pilgrimFirstname}
                    onChange={(e) => setFirstname(e.target.value)}
                    disabled={disableUserIdentityFields}
                    className="h-10 rounded-lg border-[#d7ded9] focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20"
                  />

                  <p className="text-xs leading-[18px] text-gray-500">Dans le même ordre que sur le CIP</p>
                </div>
                <div className="space-y-[5px]">
                  <Label htmlFor="edit-lastname">{Locales.base.field.last_name}*</Label>
                  <Input
                    id="edit-lastname"
                    placeholder={Locales.base.field.placeholder.lastname}
                    value={pilgrimLastname}
                    onChange={(e) => setLastname(e.target.value)}
                    disabled={disableUserIdentityFields}
                    className="h-10 rounded-lg border-[#d7ded9] focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20"
                  />
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                <div className="space-y-[5px]">
                  <Label htmlFor="npi">{Locales.base.field.npi}*</Label>
                  <Input
                    id="npi"
                    placeholder={Locales.base.field.placeholder.npi}
                    value={pilgrimNPI}
                    onChange={(e) => setNPI(e.target.value)}
                    maxLength={10}
                    disabled={disableUserIdentityFields}
                    className={cn(
                      'h-10 rounded-lg border-[#d7ded9] focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20',
                      (npiError || emptyNPIError) && 'border-red-500 focus-visible:ring-red-500'
                    )}
                  />
                  {(npiError || emptyNPIError) && <p className="text-sm text-red-600">{Locales.trip.errors.npi}</p>}
                </div>

                <div className="space-y-[5px]">
                  <Label htmlFor="edit-birthdate">{Locales.base.field.birthdate}*</Label>
                  <Popover modal open={openBirthdatePopover} onOpenChange={setOpenBirthdatePopover}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={disableUserIdentityFields}
                        id="edit-birthdate"
                        aria-label="Date de naissance"
                        className={cn(
                          'h-10 w-full justify-between rounded-lg border-[#d7ded9] px-3 text-left font-normal focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20',
                          !pilgrimBirthdate && 'text-gray-500',
                          birthDateError && 'border-red-500'
                        )}
                      >
                        <div className="flex w-full items-center justify-between pointer-events-none">
                          <span className="notranslate" translate="no">
                            {pilgrimBirthdate ? formatDate(pilgrimBirthdate, 'dd-MM-yyyy') : 'JJ-MM-AAAA'}
                          </span>
                          <CalendarIcon className="size-4 text-gray-500" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        key={openBirthdatePopover ? 'openBirthday' : 'closedBirthday'}
                        mode="single"
                        selected={pilgrimBirthdate}
                        onSelect={handleBirthDateChange}
                        disabled={(date) =>
                          date > new Date() || date < new Date('1900-01-01') || disableUserIdentityFields
                        }
                        locale={fr}
                        month={birthdateMonth}
                        onMonthChange={setBirthdateMonth}
                      />
                    </PopoverContent>
                  </Popover>
                  {birthDateError && <p className="text-sm text-red-600">{birthDateError}</p>}
                </div>
              </div>

              {!isPreviousCampaignPilgrim && (
                <fieldset className="mt-3 space-y-[5px]">
                  <legend className="text-sm text-gray-800">Sexe*</legend>
                  <RadioGroup
                    aria-label="Sexe du pèlerin"
                    aria-required="true"
                    aria-invalid={emptyFieldError && !pilgrimSex}
                    value={pilgrimSex}
                    onValueChange={handleSexChange}
                    className="grid h-[42px] max-w-[312px] grid-cols-2 gap-0 overflow-hidden rounded-lg border border-[#d7ded9] bg-white"
                  >
                    {[
                      ['M', 'Homme'],
                      ['F', 'Femme'],
                    ].map(([value, label]) => (
                      <Label
                        key={value}
                        htmlFor={`edit-sex-${value}`}
                        className={cn(
                          'relative flex cursor-pointer items-center justify-center text-sm font-normal text-gray-500 transition-colors focus-within:z-10 focus-within:ring-2 focus-within:ring-[#287c50]/30 focus-within:ring-offset-1',
                          value !== 'M' && (pilgrimSex ? 'border-l border-transparent' : 'border-l border-[#d7ded9]'),
                          pilgrimSex === value &&
                            'bg-[#eaf5ed] font-medium text-[#246641] shadow-[inset_0_0_0_1px_#96bea3]'
                        )}
                      >
                        <RadioGroupItem
                          id={`edit-sex-${value}`}
                          value={value}
                          className="absolute inset-0 h-full w-full cursor-pointer rounded-none border-0 opacity-0"
                        />
                        {label}
                      </Label>
                    ))}
                  </RadioGroup>
                </fieldset>
              )}
            </section>

            <section aria-labelledby="edit-passport-heading" className="border-t border-gray-200 pt-4">
              <h3 id="edit-passport-heading" className="mb-3 text-[13px] font-semibold text-[#35473b]">
                Passeport
              </h3>
              <div className="grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">
                <div className="space-y-[5px]">
                  <Label htmlFor="edit-passport">{Locales.base.field.passport}</Label>
                  <Input
                    id="edit-passport"
                    placeholder={Locales.base.field.placeholder.passport}
                    value={pilgrimPassport}
                    onChange={(e) => handlePassportChange(e.target.value)}
                    maxLength={10}
                    disabled={disablePassportField}
                    className={cn(
                      'h-10 rounded-lg border-[#d7ded9] focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20',
                      passportError && 'border-red-500 focus-visible:ring-red-500'
                    )}
                  />
                  {passportError && <p className="text-sm text-red-600">{Locales.trip.errors.passport}</p>}
                </div>

                <div className="space-y-[5px]">
                  <Label htmlFor="edit-passport-expiration">{Locales.base.field.passport_expiration}</Label>
                  <Popover modal open={openPassportPopover} onOpenChange={setOpenPassportPopover}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={disablePassportField}
                        id="edit-passport-expiration"
                        aria-label="Date d'expiration du passeport"
                        className={cn(
                          'h-10 w-full justify-between rounded-lg border-[#d7ded9] px-3 text-left font-normal focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20',
                          !pilgrimPassportExpirationDate && 'text-gray-500',
                          passportExpirationDateError && 'border-red-500'
                        )}
                      >
                        <div className="flex w-full items-center justify-between pointer-events-none">
                          <span className="notranslate" translate="no">
                            {pilgrimPassportExpirationDate
                              ? formatDate(pilgrimPassportExpirationDate, 'dd-MM-yyyy')
                              : 'JJ-MM-AAAA'}
                          </span>
                          <CalendarIcon className="size-4 text-gray-500" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        key={openPassportPopover ? 'openPassport' : 'closedPassport'}
                        mode="single"
                        selected={pilgrimPassportExpirationDate}
                        onSelect={handleExpirationDateChange}
                        disabled={(date) => date < passportMinExpirationDate || disablePassportField}
                        locale={fr}
                        month={expirationMonth}
                        onMonthChange={setExpirationMonth}
                        fromYear={2025}
                        toYear={2035}
                      />
                    </PopoverContent>
                  </Popover>
                  {passportExpirationDateError && <p className="text-sm text-red-600">{passportExpirationDateError}</p>}
                </div>
              </div>
            </section>

            <section aria-labelledby="edit-category-heading" className="space-y-3 border-t border-gray-200 pt-4">
              <h3 id="edit-category-heading" className="text-[13px] font-semibold text-[#35473b]">
                Catégorie et origine
              </h3>
              <div className="space-y-[5px]">
                <Label>{Locales.trip.table.categorie}*</Label>
                <Select value={pilgrimCategory} onValueChange={setPilgrimCategory} disabled={disableCategoryField}>
                  <SelectTrigger className="h-10 rounded-lg border-[#d7ded9] focus:ring-[#287c50]/20">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {openCategoriesModal && (
                  <Button
                    variant="link"
                    className="h-auto gap-2 p-0 text-[13px] font-medium"
                    onClick={openCategoriesModal}
                  >
                    {Locales.trip.categories.help_text} <ArrowRight className="size-3.5" />
                  </Button>
                )}
              </div>

              {pilgrimIsInternational && (
                <div className="space-y-[5px]">
                  <Label htmlFor="nic">Numero NIC</Label>

                  <Input
                    id="nic"
                    placeholder="Saisir le numéro NIC"
                    value={pilgrimNIC}
                    onChange={(e) => setPilgrimNIC(e.target.value)}
                    maxLength={10}
                    className={cn(
                      'h-10 rounded-lg border-[#d7ded9] focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20',
                      (nicError || emptyNicError) && 'border-red-500 focus-visible:ring-red-500'
                    )}
                  />

                  <p className="flex flex-wrap items-center gap-1 text-xs text-gray-500">
                    Vous n&apos;avez pas de NIC ?{' '}
                    <a
                      href="https://service-public.bj/public/services/service/PS01351"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-hajj-primary underline underline-offset-2"
                    >
                      Faire une demande <ExternalLink className="size-3" />
                    </a>
                  </p>

                  {nicError && <p className="text-sm text-red-600">{Locales.trip.errors.nic}</p>}
                  {emptyNicError && <p className="text-sm text-red-600">{Locales.trip.errors.empty_nic}</p>}
                </div>
              )}

              <div className="space-y-[5px]">
                <Label>Département d&apos;origine</Label>
                <Select value={pilgrimDepartment} onValueChange={setPilgrimDepartment}>
                  <SelectTrigger className="h-10 rounded-lg border-[#d7ded9] focus:ring-[#287c50]/20">
                    <SelectValue placeholder="Sélectionner un département" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTEMENTS.map((dep) => (
                      <SelectItem key={dep} value={dep}>
                        {dep}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            {!isPilgrimUser && (
              <section aria-labelledby="edit-note-heading" className="border-t border-gray-200 pt-4">
                <h3 id="edit-note-heading" className="mb-2 text-[13px] font-semibold text-[#35473b]">
                  {Locales.base.field.note}
                </h3>
                <Textarea
                  id="edit-note"
                  className="min-h-16 rounded-lg border-[#d7ded9] focus-visible:border-[#287c50] focus-visible:ring-[#287c50]/20"
                  placeholder="Ajouter une note..."
                  value={pilgrimNote}
                  onChange={(e) => setPilgrimNote(e.target.value)}
                />
              </section>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 flex-row items-center justify-between gap-3 border-t border-gray-200 px-[18px] py-3 sm:px-[26px]">
          <p className="mr-auto text-xs text-gray-500">* Champs obligatoires</p>
          <Button variant="outline" type="button" className="border-[#d7ded9]" onClick={closeModal}>
            {Locales.base.button.discard}
          </Button>
          <Button variant="hajj" type="button" disabled={isMutating} onClick={validateForm}>
            {Locales.base.button.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

EditTripModal.propTypes = {
  isOpened: PropTypes.bool,
  isPilgrimUser: PropTypes.bool,
  pilgrimData: PropTypes.shape({}),
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
    })
  ),
  openCategoriesModal: PropTypes.func,
  closeModal: PropTypes.func,
};

export default EditTripModal;
